import { useState, useRef, useEffect, useCallback } from 'react'

/**
 * YouTubePlayer - Audio-only YouTube player for voice calls
 * Uses YouTube IFrame API to play audio in sync with friends
 */

// YouTube regex to extract video ID
const YOUTUBE_REGEX = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/

export function extractYouTubeId(url) {
  const match = url?.match(YOUTUBE_REGEX)
  return match ? match[1] : null
}

export function containsYouTubeLink(text) {
  return YOUTUBE_REGEX.test(text || '')
}

export function getYouTubeThumbnail(videoId, quality = 'mqdefault') {
  return `https://img.youtube.com/vi/${videoId}/${quality}.jpg`
}

export default function YouTubePlayer({
  videoId,
  title,
  sharedBy,
  onClose,
  onPlayStateChange,
  onTimeUpdate,
  syncedState, // 'playing' | 'paused' from data channel
  syncedTime,  // Current time from data channel
  isHost       // Whether this user shared the video (controls sync)
}) {
  const [isPlaying, setIsPlaying] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [volume, setVolume] = useState(80)

  const playerRef = useRef(null)
  const containerRef = useRef(null)
  const timeUpdateInterval = useRef(null)

  // Load YouTube IFrame API
  useEffect(() => {
    if (!videoId) return

    // Load the YouTube IFrame API if not already loaded
    if (!window.YT) {
      const tag = document.createElement('script')
      tag.src = 'https://www.youtube.com/iframe_api'
      const firstScriptTag = document.getElementsByTagName('script')[0]
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)

      window.onYouTubeIframeAPIReady = initPlayer
    } else {
      initPlayer()
    }

    return () => {
      if (timeUpdateInterval.current) {
        clearInterval(timeUpdateInterval.current)
      }
      if (playerRef.current) {
        playerRef.current.destroy()
        playerRef.current = null
      }
    }
  }, [videoId])

  const initPlayer = () => {
    if (!window.YT || !window.YT.Player) {
      // API not ready yet, wait a bit
      setTimeout(initPlayer, 100)
      return
    }

    if (playerRef.current) {
      playerRef.current.destroy()
    }

    playerRef.current = new window.YT.Player(containerRef.current, {
      height: '0',   // Hidden video
      width: '0',
      videoId: videoId,
      playerVars: {
        autoplay: 0,
        controls: 0,
        disablekb: 1,
        fs: 0,
        modestbranding: 1,
        rel: 0,
      },
      events: {
        onReady: onPlayerReady,
        onStateChange: onPlayerStateChange,
      },
    })
  }

  const onPlayerReady = (event) => {
    setIsLoading(false)
    setDuration(event.target.getDuration())
    event.target.setVolume(volume)

    // Start time tracking
    timeUpdateInterval.current = setInterval(() => {
      if (playerRef.current?.getCurrentTime) {
        setCurrentTime(playerRef.current.getCurrentTime())
      }
    }, 1000)
  }

  const onPlayerStateChange = (event) => {
    const YT = window.YT
    if (event.data === YT.PlayerState.PLAYING) {
      setIsPlaying(true)
      onPlayStateChange?.('playing', playerRef.current.getCurrentTime())
    } else if (event.data === YT.PlayerState.PAUSED) {
      setIsPlaying(false)
      onPlayStateChange?.('paused', playerRef.current.getCurrentTime())
    } else if (event.data === YT.PlayerState.ENDED) {
      setIsPlaying(false)
      setCurrentTime(0)
      onPlayStateChange?.('paused', 0)
    }
  }

  // Sync from remote (if not host)
  useEffect(() => {
    if (!playerRef.current || isHost) return

    if (syncedState === 'playing' && !isPlaying) {
      playerRef.current.playVideo()
    } else if (syncedState === 'paused' && isPlaying) {
      playerRef.current.pauseVideo()
    }

    // Sync time if difference is > 2 seconds
    if (syncedTime !== undefined && Math.abs(currentTime - syncedTime) > 2) {
      playerRef.current.seekTo(syncedTime, true)
    }
  }, [syncedState, syncedTime, isHost])

  const togglePlay = useCallback(() => {
    if (!playerRef.current) return

    if (isPlaying) {
      playerRef.current.pauseVideo()
    } else {
      playerRef.current.playVideo()
    }
  }, [isPlaying])

  const handleSeek = useCallback((e) => {
    if (!playerRef.current) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const percent = x / rect.width
    const newTime = percent * duration
    playerRef.current.seekTo(newTime, true)
    setCurrentTime(newTime)
    onTimeUpdate?.(newTime)
  }, [duration, onTimeUpdate])

  const handleVolumeChange = useCallback((e) => {
    const newVolume = parseInt(e.target.value)
    setVolume(newVolume)
    if (playerRef.current) {
      playerRef.current.setVolume(newVolume)
    }
  }, [])

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  if (!videoId) return null

  return (
    <div className="glass-strong rounded-xl p-4 border border-gjyl-indigo-500/30">
      {/* Hidden YouTube Player */}
      <div ref={containerRef} className="hidden"></div>

      {/* Player UI */}
      <div className="flex items-start space-x-4">
        {/* Thumbnail */}
        <div className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gjyl-slate-800">
          <img
            src={getYouTubeThumbnail(videoId, 'mqdefault')}
            alt={title}
            className="w-full h-full object-cover"
          />
          {isLoading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
            </div>
          )}
        </div>

        {/* Info & Controls */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <div className="min-w-0">
              <h4 className="text-white font-medium text-sm truncate">
                {title || 'YouTube Video'}
              </h4>
              <p className="text-xs text-slate-400">
                Shared by {sharedBy}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-gjyl-slate-700/50 rounded-lg transition-colors text-slate-400 hover:text-white"
              title="Stop Playing"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          {/* Progress Bar */}
          <div
            className="h-1.5 bg-gjyl-slate-700 rounded-full cursor-pointer mb-2"
            onClick={handleSeek}
          >
            <div
              className="h-full bg-gjyl-indigo-500 rounded-full transition-all duration-100"
              style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
            ></div>
          </div>

          {/* Time & Controls */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {/* Play/Pause */}
              <button
                onClick={togglePlay}
                disabled={isLoading}
                className="p-2 bg-gjyl-indigo-500 hover:bg-gjyl-indigo-600 rounded-full text-white transition-colors disabled:opacity-50"
              >
                {isPlaying ? (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                )}
              </button>

              {/* Time Display */}
              <span className="text-xs text-slate-400">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            {/* Volume */}
            <div className="flex items-center space-x-2">
              <svg className="w-4 h-4 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.707.707L4.586 13H2a1 1 0 01-1-1V8a1 1 0 011-1h2.586l3.707-3.707a1 1 0 011.09-.217zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              <input
                type="range"
                min="0"
                max="100"
                value={volume}
                onChange={handleVolumeChange}
                className="w-16 h-1 bg-gjyl-slate-700 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:rounded-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Listening Together Badge */}
      <div className="mt-3 flex items-center justify-center space-x-2 text-xs text-gjyl-indigo-400">
        <span className="w-2 h-2 bg-gjyl-indigo-500 rounded-full animate-pulse"></span>
        <span>Listening Together</span>
      </div>
    </div>
  )
}
